import { ITEM_TYPES, KARATS, SORT_OPTIONS } from "./data.jsx";

const PUBLIC_PREFS_STORAGE_KEY = "karatscan:public-prefs:v1";
const PUBLIC_PREFS_VERSION = 1;

const PUBLIC_FEEDS = ["todays_deals", "deep_discount", "steal", "deal", "negotiable", "unscored_gold", "all"];
const THEMES = ["light", "dim"];
const DENSITIES = ["compact", "comfortable"];
const FEED_LAYOUTS = ["grid", "list"];
const SORTS = SORT_OPTIONS.map((option) => option.value);

const DEFAULT_PUBLIC_PREFS = Object.freeze({
  version: PUBLIC_PREFS_VERSION,
  theme: "light",
  density: "compact",
  feedLayout: "grid",
  feed: "todays_deals",
  sort: "newest",
  filters: Object.freeze({
    karat: Object.freeze([]),
    type: Object.freeze([]),
    includeReviewRequired: true,
  }),
});

const allowed = (values) => new Set(values);
const THEME_VALUES = allowed(THEMES);
const DENSITY_VALUES = allowed(DENSITIES);
const FEED_LAYOUT_VALUES = allowed(FEED_LAYOUTS);
const FEED_VALUES = allowed(PUBLIC_FEEDS);
const SORT_VALUES = allowed(SORTS);
const KARAT_VALUES = allowed(KARATS);
const ITEM_TYPE_VALUES = allowed(ITEM_TYPES);

const storageFromBrowser = () => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage || null;
  } catch {
    return null;
  }
};

const enumValue = (value, values, fallback) =>
  values.has(value) ? value : fallback;

const uniqueAllowed = (values, allowedValues, order) => {
  if (!Array.isArray(values)) return [];
  const selected = new Set(values.filter((value) => allowedValues.has(value)));
  return order.filter((value) => selected.has(value));
};

const normalizeKaratFilters = (values) => {
  if (!Array.isArray(values)) return [];
  const selected = new Set(
    values
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && KARAT_VALUES.has(value)),
  );
  return KARATS.filter((value) => selected.has(value));
};

function normalizePublicPrefs(candidate = {}) {
  const source = candidate && typeof candidate === "object" ? candidate : {};
  const filters = source.filters && typeof source.filters === "object" ? source.filters : {};
  return {
    version: PUBLIC_PREFS_VERSION,
    theme: enumValue(source.theme, THEME_VALUES, DEFAULT_PUBLIC_PREFS.theme),
    density: enumValue(source.density, DENSITY_VALUES, DEFAULT_PUBLIC_PREFS.density),
    feedLayout: enumValue(source.feedLayout, FEED_LAYOUT_VALUES, DEFAULT_PUBLIC_PREFS.feedLayout),
    feed: enumValue(source.feed, FEED_VALUES, DEFAULT_PUBLIC_PREFS.feed),
    sort: enumValue(source.sort, SORT_VALUES, DEFAULT_PUBLIC_PREFS.sort),
    filters: {
      karat: normalizeKaratFilters(filters.karat),
      type: uniqueAllowed(filters.type, ITEM_TYPE_VALUES, ITEM_TYPES),
      includeReviewRequired:
        typeof filters.includeReviewRequired === "boolean"
          ? filters.includeReviewRequired
          : DEFAULT_PUBLIC_PREFS.filters.includeReviewRequired,
    },
  };
}

function parsePublicPrefs(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || parsed.version !== PUBLIC_PREFS_VERSION) {
      return null;
    }
    return normalizePublicPrefs(parsed);
  } catch {
    return null;
  }
}

function loadPublicPrefs(storage = storageFromBrowser()) {
  if (!storage) return null;
  try {
    return parsePublicPrefs(storage.getItem(PUBLIC_PREFS_STORAGE_KEY));
  } catch {
    return null;
  }
}

function savePublicPrefs(snapshot, storage = storageFromBrowser()) {
  if (!storage) return false;
  try {
    storage.setItem(PUBLIC_PREFS_STORAGE_KEY, JSON.stringify(normalizePublicPrefs(snapshot)));
    return true;
  } catch {
    return false;
  }
}

function publicPrefsSnapshot({ tweaks = {}, feed, sort, filters } = {}) {
  return normalizePublicPrefs({
    theme: tweaks.theme,
    density: tweaks.density,
    feedLayout: tweaks.feedLayout,
    feed,
    sort,
    filters,
  });
}

export {
  DEFAULT_PUBLIC_PREFS,
  PUBLIC_PREFS_STORAGE_KEY,
  PUBLIC_PREFS_VERSION,
  loadPublicPrefs,
  normalizePublicPrefs,
  parsePublicPrefs,
  publicPrefsSnapshot,
  savePublicPrefs,
};
